import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import ChatIcon from '@mui/icons-material/Chat';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import {
  Box, Paper, Typography, Card, CardContent,
  Divider, List, ListItem, ListItemText, ListItemButton,
  ListItemIcon, Button, Chip
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/ui/PageLayout';

import { Document } from '../api/ApiClient';
import { useMyGroups } from '../hooks/api/useGroups';
import { useApiClient } from '../hooks/api/useApiClient';
import { usePermissions } from '../hooks/permissions/usePermissions';
import { useSystem } from '../hooks/api/config';
import AuthService from '../services/AuthService';
import { log } from '../utils/errors/logger';
import { formatDistanceToNow } from '../utils/formatting/dateUtils';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

// Stat card component
const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box 
          sx={{ 
            backgroundColor: `${color}.light`, 
            p: 1, 
            borderRadius: 1, 
            mr: 2,
            color: `${color}.main`
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" component="div">
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

interface Stats {
  documents?: {
    total?: number;
  };
  users?: {
    total?: number;
  };
  chat_sessions?: number;
}


const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<Stats | null>(null);
  const [recentDocuments, setRecentDocuments] = useState<Document[]>([]);
  // Use React Query for groups
  const { data: myGroupsResponse, isLoading: groupsLoading, error: groupsError } = useMyGroups();
  const myGroups = myGroupsResponse?.groups || [];

  // Debug logging for groups
  useEffect(() => {
    if (groupsError) {
      log.error('Groups query error', { error: groupsError });
    }
    if (myGroups && myGroups.length > 0) {
      log.info('Groups loaded successfully', { groupCount: myGroups.length, groups: myGroups });
    }
  }, [myGroups, groupsError]);

  // Helper function to translate group source tags
  const translateGroupSource = (tag: string): string => {
    const translations = t('dashboard.groups.sources', { returnObjects: true }) as Record<string, string>;
    return translations[tag] || tag;
  };
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const user = AuthService.getUser();
  const { can } = usePermissions();
  const isAdmin = can.configureChatModels; // Admin permission check
  
  // Check system availability  
  const { system, isLoading: systemLoading } = useSystem();

  useEffect(() => {
    // If system is still loading, don't load other data
    if (systemLoading) {
      return;
    }
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Get system stats if admin
        if (isAdmin) {
          const statsResponse = await apiClient.getSystemStatsApiAdminStatsSystemGet();
          setStats(statsResponse.data);
        }
        
        // Get recently updated documents (last 72 hours)
        const seventyTwoHoursAgo = new Date();
        seventyTwoHoursAgo.setHours(seventyTwoHoursAgo.getHours() - 72);

        const documentsResponse = await apiClient.getDocumentsApiDocumentsGet({
          skip: 0,
          limit: 10,
          updated_at_gte: seventyTwoHoursAgo.toISOString(),
          sort_by: 'recently_updated'
        });
        setRecentDocuments(documentsResponse.data.documents);
        
        // Groups are now handled by React Query hook
      } catch (error) {
        log.error('Error fetching dashboard data', { error: error instanceof Error ? error.message : String(error) });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [isAdmin, systemLoading]);

  return (
    <PageLayout
      title={t('dashboard.title')}
      isLoading={loading || systemLoading}
      maxWidth="lg"
    >
      {/* Welcome Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          {t('dashboard.welcome', { name: user?.full_name || user?.username || 'User' })}
        </Typography>
        <Typography variant="body1">
          {t('dashboard.intro', { systemName: system?.name || 'smartDoc' })}
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<ChatIcon />}
            onClick={() => navigate('/chat')}
            sx={{ mr: 2 }}
          >
            {t('dashboard.buttons.startChat')}
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<DescriptionIcon />}
            onClick={() => navigate('/documents')}
          >
            {t('dashboard.buttons.browseDocuments')}
          </Button>
        </Box>
      </Paper>
      
      {/* Stats Section */}
      {isAdmin && stats && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            {t('dashboard.stats.title')}
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title={t('dashboard.stats.totalDocuments')}
                value={stats.documents?.total || 0}
                icon={<DescriptionIcon />}
                color="primary"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title={t('dashboard.stats.totalUsers')}
                value={stats.users?.total || 0}
                icon={<PersonIcon />}
                color="secondary"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title={t('dashboard.stats.totalChats')}
                value={stats.chats?.total || 0}
                icon={<ChatIcon />}
                color="success"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title={t('dashboard.stats.chatsLast24h')}
                value={stats.chats?.last_24h || 0}
                icon={<ChatIcon />}
                color="info"
              />
            </Grid>
          </Grid>
        </Box>
      )}
      
      {/* Recent Documents Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t('dashboard.recentDocuments.title')}
        </Typography>
        <Paper>
          <List>
            {recentDocuments.length > 0 ? (
              recentDocuments.map((doc, index) => [
                index > 0 && <Divider component="li" key={`divider-${doc.document_id}`} />,
                <ListItemButton 
                  key={`item-${doc.document_id}`}
                  onClick={() => navigate(`/documents?id=${doc.document_id}`)}
                >
                  <ListItemIcon>
                    <AutoStoriesIcon color={doc.source_type === 'sap' ? 'primary' : 'secondary'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.title}
                    secondary={`${t('dashboard.recentDocuments.updated')}: ${formatDistanceToNow(new Date(doc.updated_at), { addSuffix: true })} | ${t('dashboard.recentDocuments.category')}: ${doc.category} | ${t('dashboard.recentDocuments.source')}: ${doc.source_type}`}
                  />
                </ListItemButton>
              ].filter(Boolean))
            ) : (
              <ListItem key="no-documents">
                <ListItemText primary={t('dashboard.recentDocuments.noDocuments')} />
              </ListItem>
            )}
          </List>
        </Paper>
      </Box>
      
      {/* My Groups Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t('dashboard.groups.title')}
        </Typography>
        <Paper>
          <List>
            {myGroups.length > 0 ? (
              myGroups.slice(0, 5).map((group, index) => [
                index > 0 && <Divider component="li" key={`divider-${group.id}`} />,
                <ListItemButton 
                  key={`item-${group.id}`}
                  onClick={() => navigate('/groups')}
                >
                  <ListItemIcon>
                    <GroupIcon color={group.type === 'role' ? 'primary' : group.type === 'team' ? 'secondary' : 'action'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={group.displayName || group.name || t('dashboard.groups.unnamedGroup')}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        {/* <Typography variant="caption" color="text.secondary">
                          {t('dashboard.groups.memberCount', { count: group.member_count || 0 })}
                        </Typography> */}
                        {group.source && (
                          <>
                            {/* <Typography variant="caption" color="text.secondary">•</Typography> */}
                            <Chip
                              label={translateGroupSource(group.source)}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.7rem' }}
                            />
                          </>
                        )}
                        {group.tags && group.tags.length > 0 && (
                          <>
                            <Typography variant="caption" color="text.secondary">•</Typography>
                            {group.tags.slice(0, 2).map((tag) => (
                              <Chip 
                                key={tag}
                                label={translateGroupSource(tag)} 
                                size="small" 
                                variant="outlined"
                                sx={{ height: 18, fontSize: '0.7rem' }}
                              />
                            ))}
                            {group.tags.length > 2 && (
                              <Typography variant="caption" color="text.secondary">
                                {t('dashboard.groups.moreTags', { count: group.tags.length - 2 })}
                              </Typography>
                            )}
                          </>
                        )}
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItemButton>
              ].filter(Boolean))
            ) : (
              <ListItem key="no-groups">
                <ListItemText 
                  primary={t('dashboard.groups.noGroups')} 
                  secondary={t('dashboard.groups.noGroupsDescription')}
                />
              </ListItem>
            )}
          </List>
          {myGroups.length > 0 && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => navigate('/groups')}
              >
                {t('dashboard.groups.viewAll')}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
      
      {/* Getting Started Section */}
      <Box>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t('dashboard.gettingStarted.title')}
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Typography variant="body1" paragraph>
            {t('dashboard.gettingStarted.intro', { systemName: system?.name || 'smartDoc' })}
          </Typography>
          <List>
            {[
              <ListItem key="chat-interface">
                <ListItemIcon><ChatIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary={t('dashboard.gettingStarted.useChatInterface')} 
                  secondary={t('dashboard.gettingStarted.useChatDescription')} 
                />
              </ListItem>,
              <ListItem key="browse-documents">
                <ListItemIcon><DescriptionIcon color="primary" /></ListItemIcon>
                <ListItemText 
                  primary={t('dashboard.gettingStarted.browseDocuments')} 
                  secondary={t('dashboard.gettingStarted.browseDescription')} 
                />
              </ListItem>,
              ...(user?.role !== 'standard' ? [
                <ListItem key="add-custom-documents">
                  <ListItemIcon><DescriptionIcon color="secondary" /></ListItemIcon>
                  <ListItemText 
                    primary={t('dashboard.gettingStarted.addCustomDocuments')} 
                    secondary={t('dashboard.gettingStarted.addCustomDescription')} 
                  />
                </ListItem>
              ] : [])
            ]}
          </List>
        </Paper>
      </Box>
    </PageLayout>
  );
};

export default Dashboard;
